import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ArticleCard from "../components/ArticleCard";
const IssueDetailPage = () => {
  const { volumeId, issueId } = useParams();
  const [issueData, setIssueData] = useState(null);
  const [volumeInfo, setVolumeInfo] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadIssueData();
  }, [volumeId, issueId]);
  const loadIssueData = async () => {
    try {
      const [volumesResponse, volumesResponse1] = await Promise.all([
        fetch(
          "/json/volumes/volumes/current-pharmaceutical-analysis-volume.json"
        ),
        fetch(
          "/json/volumes/volumes/current-molecular-pharmacology-volume.json"
        ),
      ]);

      // 检查两个请求是否都成功
      if (!volumesResponse.ok || !volumesResponse1.ok) {
        throw new Error("Failed to fetch volumes data");
      }

      // 同时解析两个响应的JSON数据
      const [pharmaceuticalData, molecularData] = await Promise.all([
        volumesResponse.json(),
        volumesResponse1.json(),
      ]);

      // 合并两个接口的数据到volumesData
      const volumesData = [
        ...pharmaceuticalData.volumes,
        ...molecularData.volumes,
      ];

      // 找到对应的volume和issue
      const volume = volumesData.find((v) => v.id === parseInt(volumeId));
      if (!volume) {
        throw new Error(`Volume ${volumeId} not found`);
      }

      const issue = volume.issues.find((i) => i.id === parseInt(issueId));
      // console.log("issue", issue);
      if (!issue) {
        throw new Error(`Issue ${issueId} not found in Volume ${volumeId}`);
      }

      setVolumeInfo(volume);
      setIssueData(issue);

      // 优化后的文章数据请求部分，增加错误处理和调试信息
      const [articlesResponse1, articlesResponse2] = await Promise.all([
        fetch(
          `/json/volumes/current-pharmaceutical-analysis-issue/${issue.data_file}`
        ),
        fetch(
          `/json/volumes/current-molecular-pharmacology-issue/${issue.data_file}`
        ),
      ]);

      // 只有当请求成功时才尝试解析JSON
      const articlesData1 = articlesResponse1.ok
        ? await articlesResponse1.json().catch((err) => {
            //   console.error("Failed to parse pharmaceutical articles JSON:", err);
            return [];
          })
        : [];

      const articlesData2 = articlesResponse2.ok
        ? await articlesResponse2.json().catch((err) => {
            //   console.error("Failed to parse molecular articles JSON:", err);
            return [];
          })
        : [];

      // 合并有效数据
      const allArticles = [...(articlesData1 || []), ...(articlesData2 || [])];
      setArticles(allArticles);

      // 如果两个请求都失败，抛出错误
      if (!articlesResponse1.ok && !articlesResponse2.ok) {
        throw new Error(
          `Failed to fetch articles data from both sources for ${issue.data_file}`
        );
      }
      setLoading(false);
    } catch (error) {
      console.error("Error loading issue data:", error);
      setError(error.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading issue details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Issue</h4>
          <p>{error}</p>
          <Link to="/archives" className="btn btn-primary">
            Back to Archives
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4 fade-in-up">
      {/* 面包屑导航 */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/archives">Archives</Link>
          </li>
          <li className="breadcrumb-item">{volumeInfo?.title}</li>
          <li className="breadcrumb-item active" aria-current="page">
            {issueData?.title}
          </li>
        </ol>
      </nav>

      {/* 期刊头部信息 */}
      <div className="row mb-5">
        <div className="col-md-3">
          <img
            src={issueData?.cover_img}
            className="img-fluid rounded shadow"
            alt={issueData?.title}
            onError={(e) => {
              e.target.src = "/images/placeholder-volume.jpg";
            }}
          />
        </div>
        <div className="col-md-9">
          <div className="issue-header">
            <h1 className="display-6 mb-2">
              {volumeInfo?.title} - {issueData?.title}
            </h1>
            <div className="issue-meta mb-3">
              <div className="row">
                <div className="col-sm-6">
                  <p className="mb-1">
                    <i className="fas fa-calendar-alt me-2"></i>
                    <strong>Publication Date:</strong>{" "}
                    {new Date(issueData?.publication_date).toLocaleDateString()}
                  </p>
                  <p className="mb-1">
                    <i className="fas fa-file-alt me-2"></i>
                    <strong>Articles:</strong> {articles.length}
                  </p>
                </div>
                <div className="col-sm-6">
                  <p className="mb-1">
                    <i className="fas fa-book me-2"></i>
                    <strong>Volume:</strong> {volumeInfo?.id}
                  </p>
                </div>
                <div className="col-sm-6">
                  <p className="mb-1">
                    <i className="fas fa-book me-2"></i>
                    <strong>Year:</strong> {volumeInfo?.year}
                  </p>
                </div>
              </div>
            </div>

            <p className="lead">{volumeInfo?.description}</p>
          </div>
        </div>
      </div>

      {/* 文章列表 */}
      <div className="row">
        <div className="col-12">
          <h3 className="mb-4">Articles in this Issue</h3>

          {articles.length === 0 ? (
            <div className="alert alert-info">
              <p className="mb-0">No articles available for this issue.</p>
            </div>
          ) : (
            <div className="articles-list">
              {articles.map((article, index) => (
                <ArticleCard key={index} article={article} index={index + 1} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default IssueDetailPage;
